// iter9.3: fila de fala unica (singleton) — evita sobreposicao de audios.
// Usado por AssistenteVoz.jsx (avisos + respostas) e Configuracoes (teste de voz).
// Regra: enquanto um audio toca, novas chamadas entram em fila e aguardam.
// dedupe: ignora repeticao identica nos ultimos 5s (ex. polling que devolveria
// o mesmo alerta duas vezes).

import { AssistenteVozFalar } from './api';

const fila = [];
let tocandoAtual = null;       // HTMLAudioElement em execucao
let urlAtual = null;           // ObjectURL para revogar
let processando = false;
const ultimosTextos = [];      // { texto, ts } para dedupe

// MOBILE (iOS Safari em especial): audio.play() so roda sem gesto do usuario se o MESMO
// elemento <audio> ja tiver tocado com sucesso uma vez DENTRO de um gesto. Criar `new Audio()`
// do zero a cada fala falha silenciosamente no iPhone porque o play() real acontece depois do
// await na API (fora da janela do gesto). Por isso reusamos sempre o MESMO elemento.
let elementoAudio = null;
function pegaElementoAudio() {
    if (!elementoAudio && typeof window !== 'undefined' && typeof Audio !== 'undefined') {
        elementoAudio = new Audio();
        try { elementoAudio.setAttribute('playsinline', 'true'); } catch (_) {}
    }
    return elementoAudio;
}

// WAV minimo (1 amostra de silencio, mono 8-bit/8kHz) -- so pra ter uma fonte VALIDA no play()
// de destravarAudioMobile. play() num <audio> SEM src nao conta como reproducao bem-sucedida
// em alguns navegadores (Android incluso) -- o "destravamento" fica um no-op silencioso.
let silencioUrl = null;
function getSilencioUrl() {
    if (silencioUrl || typeof Blob === 'undefined' || typeof URL === 'undefined') return silencioUrl;
    try {
        const buf = new ArrayBuffer(45);
        const v = new DataView(buf);
        const wr = (off, s) => { for (let i = 0; i < s.length; i++) v.setUint8(off + i, s.charCodeAt(i)); };
        wr(0, 'RIFF'); v.setUint32(4, 37, true); wr(8, 'WAVE'); wr(12, 'fmt ');
        v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
        v.setUint32(24, 8000, true); v.setUint32(28, 8000, true); v.setUint16(32, 1, true); v.setUint16(34, 8, true);
        wr(36, 'data'); v.setUint32(40, 1, true); v.setUint8(44, 128);
        silencioUrl = URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
    } catch (_) { /* noop */ }
    return silencioUrl;
}

/** Chame de dentro de um clique/toque real (ex.: botao do microfone) pra destravar audio no mobile. */
export function destravarAudioMobile() {
    const a = pegaElementoAudio();
    if (!a) return;
    try {
        if (!a.src) { const u = getSilencioUrl(); if (u) a.src = u; }
        const p = a.play();
        if (p && p.catch) p.catch(() => {});
    } catch (_) { /* noop */ }
}

// Rede de seguranca: le o texto com a voz NATIVA do navegador (SpeechSynthesis) quando o audio
// do Piper falhar por qualquer motivo (autoplay bloqueado, Piper fora do ar, resposta sem audio).
// Qualidade menor que o Cadu (Piper), mas cobre TODA plataforma sem depender de fetch/Blob/<audio>.
function falarComSpeechSynthesis(texto) {
    return new Promise((ok) => {
        try {
            if (typeof window === 'undefined' || !window.speechSynthesis || !texto) return ok();
            const u = new SpeechSynthesisUtterance(texto);
            u.lang = 'pt-BR';
            let terminou = false;
            const termina = () => { if (terminou) return; terminou = true; emit('fim'); ok(); };
            u.onend = termina; u.onerror = termina;
            emit('inicio', { texto });
            window.speechSynthesis.speak(u);
            // alguns navegadores nao disparam onend de forma confiavel -- rede de seguranca por tempo.
            setTimeout(termina, Math.min(20000, 1500 + texto.length * 65));
        } catch (_) { ok(); }
    });
}

// iter9.4: bus de eventos para componentes acompanharem o estado da fala.
// 'inicio' { texto } | 'fim' | 'silenciado'
export const vozBus = (typeof window !== 'undefined') ? new EventTarget() : null;
function emit(nome, detail) { try { vozBus && vozBus.dispatchEvent(new CustomEvent(nome, { detail })); } catch (_) {} }

function ehDuplicada(texto) {
    const agora = Date.now();
    // limpa antigos
    for (let i = ultimosTextos.length - 1; i >= 0; i--) {
        if (agora - ultimosTextos[i].ts > 5000) ultimosTextos.splice(i, 1);
    }
    const t = (texto || '').trim().toLowerCase();
    if (!t) return true;
    if (ultimosTextos.some(x => x.texto === t)) return true;
    ultimosTextos.push({ texto: t, ts: agora });
    return false;
}

async function processaFila() {
    if (processando) return;
    processando = true;
    while (fila.length) { // eslint-disable-line no-loop-func
        const { texto, resolve } = fila.shift();
        try {
            const r = await AssistenteVozFalar({ texto });
            if (r instanceof Blob) {
                await new Promise((ok) => {
                    try {
                        urlAtual = URL.createObjectURL(r);
                        const a = pegaElementoAudio() || new Audio();
                        a.src = urlAtual;
                        tocandoAtual = a;
                        emit('inicio', { texto });
                        const falhou = () => {
                            try { URL.revokeObjectURL(urlAtual); } catch (_) {}
                            urlAtual = null; tocandoAtual = null; emit('fim');
                            falarComSpeechSynthesis(texto).then(ok);
                        };
                        a.onended = () => {
                            try { URL.revokeObjectURL(urlAtual); } catch (_) {}
                            urlAtual = null; tocandoAtual = null;
                            emit('fim');
                            ok();
                        };
                        a.onerror = falhou;
                        const p = a.play();
                        if (p && p.catch) p.catch(falhou); else if (!p) { /* navegadores antigos: assume ok */ }
                    } catch (_) { falarComSpeechSynthesis(texto).then(ok); }
                });
            } else {
                // Piper indisponivel/erro -- ainda assim fala o texto (voz nativa) pra nunca ficar muda.
                await falarComSpeechSynthesis(texto);
            }
        } catch (_) {}
        resolve && resolve();
    }
    processando = false;
}

/** Enfileira `texto` para ser falado quando o canal estiver livre. */
export function falar(texto) {
    if (!texto || ehDuplicada(texto)) return Promise.resolve();
    return new Promise((resolve) => {
        fila.push({ texto, resolve });
        processaFila();
    });
}

/** Para imediatamente a fala atual e limpa a fila (uso ex.: usuario clicou em "parar"). */
export function silenciar() {
    fila.length = 0;
    if (tocandoAtual) {
        try { tocandoAtual.pause(); } catch (_) {}
        try { tocandoAtual.currentTime = 0; } catch (_) {}
        tocandoAtual = null;
    }
    if (urlAtual) {
        try { URL.revokeObjectURL(urlAtual); } catch (_) {}
        urlAtual = null;
    }
    emit('silenciado');
    emit('fim');
}

/** True se ha algo tocando ou na fila. */
export function ocupado() {
    return !!tocandoAtual || fila.length > 0;
}
